import type {
  FirstDataRenderedEvent,
  GridApi,
  GridOptions,
  IsGroupOpenByDefaultParams,
  RowClassRules,
} from 'ag-grid-community';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from 'styled-components';
import type { BlotterTableClientLocalFilter, ColumnOrColumnGroup } from '..';
import { useCallbackRef } from '../../../hooks/useCallbackRef';
import { WarningSeverity } from '../../../types';
import { WARNING_ROW_CLASSNAME } from '../../AgGrid';
import { DEFAULT_BLOTTER_AGG_FUNCS } from '../aggFuncs';
import { trackAgGridContextMenuPopupClicks } from '../agGridMenuTracking';
import { useBlotterTableContext, type BlotterTableContextProps } from '../BlotterTableContext';
import { reconcilePersistedGridState } from '../blotterTableGridState';
import { withDefaultGroupCellRendererParams } from '../defaultGroupCellRendererParams';
import { BlotterDensity, extractColumns } from '../types';
import {
  alphabeticalGroupOrder,
  isGridApiReady,
  quickFilterParser,
  resetColumnsIfAllHidden,
  safeGridApi,
  syncNoRowsOverlay,
} from '../utils';
import { DEFAULT_AG_GRID_EXCEL_STYLES } from './excelExportStyling';
import type { UseBlotterTableGridOptions, UseBlotterTableProps } from './types';
import { useBlotterTableMenus, type UseBlotterTableMenusParams } from './useBlotterTableMainMenu';

export interface UseBlotterTableGridOptionsProps<TRow extends object> extends Pick<
  UseBlotterTableProps<TRow>,
  'rowID' | 'density' | 'context' | 'getExtraMainMenuItems' | 'onBlotterContextChange' | 'persistence'
> {
  /** Custom GridOptions provided with useBlotterTable hook call */
  gridOptions: UseBlotterTableGridOptions<TRow> | undefined;

  // The rest of the props are useBlotterTable inputs used to customize the gridOptions
  clientLocalFilter?: BlotterTableClientLocalFilter<TRow>;
  columns: ColumnOrColumnGroup[];
  autoGroupColumnDef?: GridOptions<TRow>['autoGroupColumnDef'];
  columnDefs: NonNullable<GridOptions['columnDefs']>;
  customBlotterContext: UseBlotterTableProps<TRow>['context'];
  getParamsFormatted: GridOptions['processCellForClipboard'];
  onGridReady: NonNullable<GridOptions<TRow>['onGridReady']>;
  rowID: UseBlotterTableProps<TRow>['rowID'];
  columnDynamicsParams?: UseBlotterTableProps<TRow>['columnDynamicsParams'];
  contextMenuOptions?: UseBlotterTableMenusParams<TRow>['contextMenuOptions'];
  exportOptions: UseBlotterTableMenusParams<TRow>['exportOptions'];
  contextMenuHandleClickJson: (item?: unknown) => void;
}

// memoization and customization of AgGrid GridOptions {@link GridOptions}
export function useBlotterTableGridOptions<TRow extends object>({
  gridOptions,
  clientLocalFilter,
  columns,
  columnDefs,
  customBlotterContext,
  density,
  getParamsFormatted,
  onGridReady,
  rowID,
  getExtraMainMenuItems,
  columnDynamicsParams,
  persistence,
  onBlotterContextChange,
  exportOptions,
  contextMenuOptions,
  contextMenuHandleClickJson,
}: UseBlotterTableGridOptionsProps<TRow>): GridOptions<TRow> {
  const [api, setApi] = useState<GridApi | null>(null);
  const {
    rowHeightBlotterTableCompact,
    rowHeightBlotterTableDefault,
    rowHeightBlotterTableComfortable,
    rowHeightBlotterTableVeryComfortable,
  } = useTheme();
  const resolvedRowHeight =
    gridOptions?.rowHeight ??
    (density === BlotterDensity.Compact
      ? rowHeightBlotterTableCompact
      : density === BlotterDensity.Comfortable
        ? rowHeightBlotterTableComfortable
        : density === BlotterDensity.VeryComfortable
          ? rowHeightBlotterTableVeryComfortable
          : rowHeightBlotterTableDefault);

  // memoize the onGridReady callback in case someone else wants to use it
  // if user provides their own onGridReady, we need to call both
  // - useCallbackRef used to avoid recreating the function on every render for custom gridOptions
  const onGridReadyCallback = useCallbackRef<NonNullable<GridOptions['onGridReady']>>(params => {
    setApi(params.api);
    // run feature-provided onGridReady
    gridOptions?.onGridReady?.(params);
    // run useGridBlottersTable's onGridReady
    onGridReady?.(params);
  });

  const rowClassRules: RowClassRules<TRow> | undefined = useMemo(() => {
    if (columns.find(c => c.type === 'warning' && c.hide !== true)) {
      return {
        [WARNING_ROW_CLASSNAME]: params =>
          (
            params?.data as {
              warningSeverity?: WarningSeverity;
            }
          )?.warningSeverity === WarningSeverity.HIGH,
      };
    }
    return undefined;
  }, [columns]);
  const mergedRowClassRules: RowClassRules<TRow> | undefined = useMemo(
    () =>
      rowClassRules != null || gridOptions?.rowClassRules != null
        ? {
            ...rowClassRules,
            ...gridOptions?.rowClassRules,
          }
        : undefined,
    [gridOptions?.rowClassRules, rowClassRules]
  );

  const getRowId = useCallback<NonNullable<GridOptions['getRowId']>>(
    ({ data }) => (rowID ? data?.[rowID] : null),
    [rowID]
  );

  const { getMainMenuItems, getContextMenuItems } = useBlotterTableMenus<TRow>({
    gridApi: safeGridApi(api),
    getExtraMainMenuItems: getExtraMainMenuItems ?? gridOptions?.getMainMenuItems,
    exportOptions,
    columnDynamicsParams,
    contextMenuOptions,
    contextMenuHandleClickJson,
  });

  // Context
  const blotterTableContext = useBlotterTableContext();
  const agGridContext = useRef<BlotterTableContextProps | null>(null);
  const previousQuickFilterContext = useRef<{
    securitiesBySymbol: BlotterTableContextProps['securitiesBySymbol'];
    includeSedolInSecuritySearch: boolean | undefined;
  }>();
  useEffect(() => {
    const context = {
      ...blotterTableContext,
      ...customBlotterContext,
    };
    const quickFilterContext = {
      securitiesBySymbol: context.securitiesBySymbol,
      includeSedolInSecuritySearch: context.settings?.includeSedolInSecuritySearch,
    };
    const quickFilterContextChanged =
      previousQuickFilterContext.current != null &&
      (previousQuickFilterContext.current.securitiesBySymbol !== quickFilterContext.securitiesBySymbol ||
        previousQuickFilterContext.current.includeSedolInSecuritySearch !==
          quickFilterContext.includeSedolInSecuritySearch);

    agGridContext.current = context;
    if (isGridApiReady(api)) {
      if (quickFilterContextChanged) {
        api.resetQuickFilter();
        if (api.isQuickFilterPresent()) {
          api.onFilterChanged('quickFilter');
        }
      }
      api.refreshCells({ force: true });
      onBlotterContextChange?.({ api });
    }
    previousQuickFilterContext.current = quickFilterContext;
  }, [api, blotterTableContext, customBlotterContext, onBlotterContextChange]);

  // - useCallbackRef used to avoid recreating the function on every render for custom gridOptions
  const onFirstDataRendered: GridOptions<TRow>['onFirstDataRendered'] = useCallbackRef(
    (params: FirstDataRenderedEvent<TRow>) => {
      // run feature-provided onFirstDataRendered
      gridOptions?.onFirstDataRendered?.(params);
      // Check on mount if all columns are hidden. If they are, we are in a bad state, so reset them.
      resetColumnsIfAllHidden(params);
    }
  );

  const onModelUpdated: GridOptions<TRow>['onModelUpdated'] = useCallbackRef(params => {
    syncNoRowsOverlay(params.api);
    gridOptions?.onModelUpdated?.(params);
  });

  // postProcessPopup is the only AG Grid hook that sees both custom and built-in context-menu items after render.
  // Keep caller-provided popup processing intact while adding Datadog tracking for BlotterTable menus.
  const postProcessPopup: NonNullable<GridOptions<TRow>['postProcessPopup']> = useCallbackRef(params => {
    trackAgGridContextMenuPopupClicks(params);
    gridOptions?.postProcessPopup?.(params);
  });

  const isGroupOpenByDefault = useIsGroupOpenByDefault({ persistence, gridOptions });

  const reconciledInitialGridState = useMemo(
    () => reconcilePersistedGridState(persistence?.initialState?.gridState, columns),
    [columns, persistence?.initialState?.gridState]
  );

  const groupingAtInit = useMemo(
    () =>
      gridOptions?.autoGroupColumnDef != null ||
      gridOptions?.treeData === true ||
      extractColumns(columns).some(column => column.rowGroup === true || column.initialRowGroup === true),
    [columns, gridOptions?.autoGroupColumnDef, gridOptions?.treeData]
  );

  const autoGroupColumnDef = useMemo(
    () => (groupingAtInit ? withDefaultGroupCellRendererParams(gridOptions?.autoGroupColumnDef ?? {}) : undefined),
    [groupingAtInit, gridOptions?.autoGroupColumnDef]
  );

  const memoizedGridOptions = useMemo(() => {
    const outputOptions: GridOptions<TRow> = {
      ...gridOptions,
      columnDefs,
      autoGroupColumnDef,
      initialState: reconciledInitialGridState,

      isGroupOpenByDefault,
      groupDefaultExpanded: undefined, // the behavior this property enables is baked into our implementation of isGroupOpenByDefault
      onFirstDataRendered,
      onModelUpdated,
      onGridReady: onGridReadyCallback,
      // Our column defs are unstable, and we use them to represent initial setup of columns. When column defs change, maintain the current column order set by the user.
      maintainColumnOrder: gridOptions?.maintainColumnOrder ?? true,
      animateRows: gridOptions?.animateRows ?? false,
      aggFuncs: { ...DEFAULT_BLOTTER_AGG_FUNCS, ...gridOptions?.aggFuncs },
      suppressAggFuncInHeader: gridOptions?.suppressAggFuncInHeader ?? true,
      rowHeight: resolvedRowHeight,
      rowClassRules: mergedRowClassRules,
      getRowId,
      getMainMenuItems,
      getContextMenuItems,
      isExternalFilterPresent: () => clientLocalFilter !== undefined,
      doesExternalFilterPass: node => clientLocalFilter?.(node) ?? true,
      cacheQuickFilter: true,
      quickFilterParser: gridOptions?.quickFilterParser ?? quickFilterParser,
      initialGroupOrderComparator: gridOptions?.initialGroupOrderComparator ?? alphabeticalGroupOrder,
      // @ts-expect-error - The context should always be set
      context: agGridContext,
      postProcessPopup,
      processCellForClipboard: gridOptions?.processCellForClipboard ?? getParamsFormatted,
      copyHeadersToClipboard: true,
      popupParent: gridOptions?.popupParent ?? document.body,
      // Default styles come first, then user-provided styles (user styles can override defaults with same id)
      excelStyles: [...DEFAULT_AG_GRID_EXCEL_STYLES, ...(gridOptions?.excelStyles ?? [])],
    };
    return outputOptions;
  }, [
    gridOptions,
    reconciledInitialGridState,
    autoGroupColumnDef,
    columnDefs,
    isGroupOpenByDefault,
    onFirstDataRendered,
    onModelUpdated,
    onGridReadyCallback,
    postProcessPopup,
    resolvedRowHeight,
    mergedRowClassRules,
    getRowId,
    getMainMenuItems,
    getContextMenuItems,
    getParamsFormatted,
    clientLocalFilter,
  ]);

  return memoizedGridOptions;
}

/**
 * This hook encapsulates custom behavior for how we want to handle group expansions in AgGrid. There are a few things we make possible that AgGrid does not natively support:
 * 1. Making "groupDefaultExpanded"-like behavior apply for more than just the first mount. We want it to keep applying until the user changes their expansion setting.
 * 2. Persisting expansion intent (expand-all, collapse-all) as a global state + exceptions instead of solely a list of all expanded IDs (which can become very large)
 */
const useIsGroupOpenByDefault = <TRow extends object>({
  persistence,
  gridOptions,
}: Pick<UseBlotterTableProps<TRow>, 'persistence' | 'gridOptions'>) => {
  const persistedRowGroupExpansionState = persistence?.state?.extraGridState?.rowGroupExpansion?.expansionState;
  const persistedExpansionExceptions = persistence?.state?.extraGridState?.rowGroupExpansion?.expansionExceptions;
  const persistedExpansionExceptionsSet = useMemo(
    () => (persistedExpansionExceptions ? new Set(persistedExpansionExceptions) : undefined),
    [persistedExpansionExceptions]
  );

  const { isGroupOpenByDefault: inputIsGroupOpenByDefault, groupDefaultExpanded } = gridOptions ?? {};

  const isGroupOpenByDefault = useCallback(
    (params: IsGroupOpenByDefaultParams<TRow>) => {
      // If the user has provided their own isGroupOpenByDefault callback, we always let this take precedence
      if (inputIsGroupOpenByDefault?.(params) === true) {
        return true;
      }

      // If expansion state is persisted, use that
      if (persistedRowGroupExpansionState) {
        const defaultIsOpen = persistedRowGroupExpansionState === 'all-expanded';

        if (params.rowNode.id && persistedExpansionExceptionsSet?.has(params.rowNode.id)) {
          return !defaultIsOpen;
        }

        return defaultIsOpen;
      }

      // Else, nothing is persisted, check if there is a groupDefaultExpanded provided into gridOptions
      if (groupDefaultExpanded) {
        if (groupDefaultExpanded === -1) {
          // -1 means all levels are to be opened
          return true;
        }

        if (typeof groupDefaultExpanded === 'number' && params.level < groupDefaultExpanded) {
          // level specificity
          return true;
        }
      }

      return false;
    },
    [inputIsGroupOpenByDefault, groupDefaultExpanded, persistedExpansionExceptionsSet, persistedRowGroupExpansionState]
  );

  return isGroupOpenByDefault;
};
